import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Brain,
  Star,
  Shield,
  Users,
  CheckCircle2,
  Sparkles,
  Building2,
  Stethoscope,
  HeartPulse,
} from "lucide-react";

const AppFrame = ({
  url,
  imgSrc,
  imgAlt,
}: {
  url: string;
  imgSrc: string;
  imgAlt: string;
}) => (
  <div className="rounded-2xl overflow-hidden shadow-[0_20px_60px_-12px_rgba(0,0,0,0.2)] border border-border/50">
    <div className="bg-[#1e1e2e] px-4 py-2.5 flex items-center gap-3">
      <div className="flex gap-1.5 shrink-0">
        <span className="block w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
        <span className="block w-2.5 h-2.5 rounded-full bg-[#ffbd2e]" />
        <span className="block w-2.5 h-2.5 rounded-full bg-[#28ca41]" />
      </div>
      <div className="flex-1 mx-3 min-w-0">
        <div className="bg-white/10 rounded px-3 py-0.5 text-[10px] text-white/50 text-center truncate">
          {url}
        </div>
      </div>
    </div>
    <img src={imgSrc} alt={imgAlt} className="w-full block" />
  </div>
);

const prismedicFeatures = [
  "AI-powered prescriptions with always-preview templates",
  "Doctor profile, portfolio & weekly slot publishing",
  "Patient bookings & context-rich intake via HealthPal",
  "Exhaustive medicine & investigation database",
  "Mobile-first; seamless with Vitalink for enterprises",
];

const vitalinkFeatures = [
  "OPD live operations — rooms, doctors & token queue in real time",
  "IPD — wards & beds, transfers, ward rounds, eMAR & discharge",
  "Emergency triage, casualty register & operation theatre scheduling",
  "Diagnostics, pharmacy, finance & billing under one roof",
  "People, assets & multi-branch administration",
  "Insights, MIS & statutory reports (HMIS, IDSP, PM-JAY)",
];

const vitalinkGallery = [
  {
    src: "/vitalink/ipd-triage.png",
    alt: "Vitalink emergency triage queue with red, yellow and green priority levels",
    caption: "IPD · Emergency triage",
  },
  {
    src: "/vitalink/analytics.png",
    alt: "Vitalink statistics and analytics — patient footfall and revenue by stream",
    caption: "Insights · Statistics & analytics",
  },
];

const healthPalFeatures = [
  "AI-based doctor & specialist search and matchmaking",
  "Family-wise health data storage and longitudinal analysis",
  "Smart health alerts, reminders & medical records vault",
  "Insurance management, claims tracking & policy insights",
  "Seamless booking via Prismedic-connected clinics",
];

const principles = [
  {
    icon: Star,
    title: "Quality First",
    description:
      "International standards in design and performance — built to compete globally, priced for India.",
    iconClass: "text-primary",
    bgClass: "bg-primary/10",
    barFrom: "from-primary",
    barTo: "to-primary/40",
    img: "/feature_affordable_quality.png",
  },
  {
    icon: Brain,
    title: "AI-Powered",
    description:
      "Cutting-edge AI integrated at every layer for smarter, faster, and more efficient healthcare delivery.",
    iconClass: "text-secondary",
    bgClass: "bg-secondary/10",
    barFrom: "from-secondary",
    barTo: "to-secondary/40",
    img: "/feature_ai_healthcare.png",
  },
  {
    icon: Shield,
    title: "Affordability",
    description:
      "Making advanced healthcare technology genuinely accessible to every clinician and patient across India.",
    iconClass: "text-accent",
    bgClass: "bg-accent/10",
    barFrom: "from-accent",
    barTo: "to-accent/40",
    img: "/feature_made_in_india.png",
  },
];

const Products = () => {
  return (
    <Layout>
      {/* ─── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative py-28 overflow-hidden bg-gradient-to-br from-primary/8 via-background to-secondary/8">
        <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/6 rounded-full blur-3xl translate-x-1/2 -translate-y-1/4 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-secondary/6 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3 pointer-events-none" />

        <div className="container mx-auto px-4 text-center relative">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-semibold mb-8">
            <Sparkles className="h-3.5 w-3.5" />
            Product Ecosystem
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1]">
            <span className="text-gradient-primary">Intelligent tools</span>
            <br />
            <span className="text-foreground">for modern healthcare</span>
          </h1>

          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-12 leading-relaxed">
            Three purpose-built products — for individual clinicians, hospitals &amp; clinics,
            and everyday health management — designed together, scaling with you.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            {[
              { icon: Stethoscope, label: "Doctor-First SaaS", sub: "Prismedic" },
              { icon: Building2, label: "Hospital Information Management", sub: "Vitalink" },
              { icon: HeartPulse, label: "Consumer Health", sub: "HealthPal" },
              { icon: Brain, label: "AI at the Core", sub: "Every feature" },
            ].map(({ icon: Icon, label, sub }) => (
              <div
                key={label}
                className="w-full md:w-auto flex items-center gap-3 px-5 py-3 rounded-2xl bg-white/80 backdrop-blur border border-border/60 shadow-sm"
              >
                <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-foreground">{label}</p>
                  <p className="text-xs text-muted-foreground">{sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Prismedic ────────────────────────────────────────────────────── */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Text */}
            <div>
              <div className="flex items-center gap-1 mb-6">
                <img src="/pm_logo_detailed_graphic.png" alt="Prismedic" className="h-16" />
                <img src="/Prismedic_logo.png" alt="Prismedic" className="h-16" />
              </div>

              <span className="text-xs font-bold tracking-[0.18em] uppercase text-primary mb-3 block">
                Doctor-First SaaS Platform
              </span>

              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 leading-tight">
                The complete clinical assistant for every practitioner
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                AI-driven prescription creation, slot &amp; portfolio management, smart patient
                booking via HealthPal, an exhaustive medicine database, and full mobile capability
                — a true expert assistant for clinicians.
              </p>

              <ul className="space-y-3 mb-10">
                {prismedicFeatures.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  asChild
                  size="lg"
                  className="group gap-2 rounded-xl shadow-[0_4px_14px_rgba(0,0,0,0.1)]"
                >
                  <Link to="/products/prismedic">
                    Explore Prismedic
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="rounded-xl border-primary/40 text-primary hover:bg-primary/5"
                >
                  <a href="https://app.prismedic.in" target="_blank" rel="noopener noreferrer">
                    Sign up or Login
                  </a>
                </Button>
              </div>
            </div>

            {/* Screenshot */}
            <div className="relative pb-6">
              <AppFrame
                url="app.prismedic.in · Rx – Prescription Writing"
                imgSrc="/prismedic-rx.png"
                imgAlt="Prismedic Prescription Writing with live PDF preview"
              />
              <div className="absolute -bottom-2 left-4 flex items-center gap-2 bg-white rounded-xl shadow-xl px-4 py-2.5 border border-border/40">
                <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs font-semibold text-foreground">AI Active · Always-Preview Mode</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Vitalink ─────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gradient-to-br from-muted/40 via-background to-muted/20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Screenshot (left on desktop) */}
            <div className="order-last lg:order-first space-y-5">
              <div className="relative pb-6">
                <AppFrame
                  url="clinic.vitalink.in · OPD / Clinic Live Operations"
                  imgSrc="/vitalink/opd-live-ops.png"
                  imgAlt="Vitalink OPD live operations — room status, doctor status and token queue"
                />
                <div className="absolute -bottom-2 right-4 flex items-center gap-2 bg-white rounded-xl shadow-xl px-4 py-2.5 border border-border/40">
                  <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
                  <span className="text-xs font-semibold text-foreground">Multi-branch · Real-time</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {vitalinkGallery.map(({ src, alt, caption }) => (
                  <figure key={src} className="space-y-2">
                    <div className="rounded-xl overflow-hidden border border-border/50 shadow-[0_8px_30px_-10px_rgba(0,0,0,0.2)] bg-white">
                      <img src={src} alt={alt} className="w-full block" loading="lazy" />
                    </div>
                    <figcaption className="text-xs font-medium text-muted-foreground text-center">
                      {caption}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>

            {/* Text (right on desktop) */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img src="/vitalink/vitalink-logo-light.png" alt="" aria-hidden="true" className="h-16 w-auto" />
                <span className="text-5xl font-bold text-foreground tracking-tight leading-none">Vitalink</span>
              </div>

              <span className="text-xs font-bold tracking-[0.18em] uppercase text-secondary mb-3 block">
                Complete Hospital Information Management System
              </span>

              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 leading-tight">
                One platform to run the entire hospital
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                A full-scale HIMS covering OPD, IPD, emergency, operation theatre, diagnostics, imaging,
                pharmacy, finance and people — with live operational dashboards, MIS insights and
                statutory reporting, across every branch of your hospital or clinic network.
              </p>

              <ul className="space-y-3 mb-10">
                {vitalinkFeatures.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle2 className="h-4 w-4 text-secondary mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <Button
                asChild
                size="lg"
                variant="outline"
                className="group gap-2 rounded-xl border-secondary/40 text-secondary hover:bg-secondary/5"
              >
                <a href="https://www.vitalink.in" target="_blank" rel="noopener noreferrer">
                  Visit Vitalink
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── HealthPal ────────────────────────────────────────────────────── */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Text */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-9 w-9 rounded-xl bg-rose-500/10 flex items-center justify-center">
                  <HeartPulse className="h-5 w-5 text-rose-500" />
                </div>
                <span className="text-2xl font-bold text-foreground tracking-tight">HealthPal</span>
              </div>

              <span className="text-xs font-bold tracking-[0.18em] uppercase text-rose-500 mb-3 block">
                Consumer Health App
              </span>

              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 leading-tight">
                Your family's health, intelligently managed
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                An AI-powered companion for everyday people — find the right doctor, store your
                family's health history, manage insurance, and stay ahead of health risks with
                smart alerts and personalised insights.
              </p>

              <ul className="space-y-3 mb-10">
                {healthPalFeatures.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle2 className="h-4 w-4 text-rose-500 mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <Button
                size="lg"
                variant="outline"
                className="group gap-2 rounded-xl border-rose-400/40 text-rose-500 hover:bg-rose-500/5"
                disabled
              >
                Coming Soon
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>

            {/* Screenshot */}
            <div className="relative pb-6">
              <AppFrame
                url="app.healthpal.in · Home"
                imgSrc="/healthpal-home.svg"
                imgAlt="HealthPal Consumer Health App"
              />
              <div className="absolute -bottom-2 left-4 flex items-center gap-2 bg-white rounded-xl shadow-xl px-4 py-2.5 border border-border/40">
                <span className="h-2 w-2 rounded-full bg-rose-500 animate-pulse" />
                <span className="text-xs font-semibold text-foreground">AI Active · Family Health Mode</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Vision / Principles ──────────────────────────────────────────── */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.18em] uppercase text-primary mb-3 block">
              Our Philosophy
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient-primary">Innovation</span> meets accessibility
            </h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              Three principles that guide everything we build.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {principles.map(({ icon: Icon, title, description, iconClass, bgClass, barFrom, barTo, img }) => (
              <div
                key={title}
                className="group relative overflow-hidden rounded-3xl border border-border/60 bg-white shadow-[0_4px_24px_rgba(0,0,0,0.06)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.12)] transition-all duration-500 flex flex-col"
              >
                <div className={`h-[3px] w-full bg-gradient-to-r ${barFrom} ${barTo}`} />
                <div className="p-8 flex-1">
                  <div className={`h-12 w-12 rounded-2xl ${bgClass} flex items-center justify-center mb-5`}>
                    <Icon className={`h-6 w-6 ${iconClass}`} />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">{title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{description}</p>
                </div>
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={img}
                    alt={title}
                    className="w-full h-full object-cover opacity-85 group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-white/70 to-transparent" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ──────────────────────────────────────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-primary/10 via-secondary/5 to-background relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
        <div className="absolute right-0 top-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute left-0 bottom-0 w-72 h-72 bg-secondary/8 rounded-full blur-3xl pointer-events-none" />

        <div className="container mx-auto px-4 relative">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-semibold mb-6">
              <Users className="h-3.5 w-3.5" />
              Early Access
            </div>

            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Be first to experience
              <br />
              <span className="text-gradient-primary">the future</span>
            </h2>

            <p className="text-muted-foreground mb-10 text-lg max-w-lg mx-auto">
              Stay updated on product launches and join early access for exclusive features before
              we go wide.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-5 py-3 rounded-xl border border-input bg-white/80 backdrop-blur text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              <Button className="gap-2 rounded-xl whitespace-nowrap shadow-[0_4px_14px_rgba(0,0,0,0.12)]">
                Notify Me
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Products;
